(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  if (!mobile.matches) return;

  document.documentElement.classList.add('vorliva-mobile-motion');

  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!gsap || !ScrollTrigger || reducedMotion) return;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const lowMotionDevice = Boolean(
    connection?.saveData ||
    (navigator.deviceMemory && navigator.deviceMemory <= 4) ||
    (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
  );
  const refinementActive = document.documentElement.classList.contains('vorliva-gsap-ready');
  document.documentElement.classList.toggle('vorliva-motion-lite', lowMotionDevice);

  gsap.registerPlugin(ScrollTrigger);
  const toArray = (scope, selector) => gsap.utils.toArray(selector, scope);

  const animateOpening = () => {
    if (refinementActive) return;
    const opening = document.querySelector('.vorliva-opening');
    if (!opening || opening.dataset.vlmOpening === 'ready') return;
    opening.dataset.vlmOpening = 'ready';
    const letters = toArray(opening, '.vorliva-opening__word span');
    const top = toArray(opening, '.vorliva-opening__top > *');
    const signature = opening.querySelector('.vorliva-opening__copy > strong');
    const subline = opening.querySelector('.vorliva-opening__copy > p');
    const journey = opening.querySelector('.vorliva-opening__journey');
    gsap.killTweensOf([...letters, ...top, signature, subline, journey].filter(Boolean));
    const timeline = gsap.timeline({ defaults: { overwrite: 'auto' } });
    timeline.fromTo(top, {
      y: -18,
      autoAlpha: 0
    }, {
      y: 0,
      autoAlpha: 1,
      duration: .62,
      stagger: .07,
      ease: 'back.out(1.7)',
      clearProps: 'transform,opacity,visibility'
    }).fromTo(subline, {
      x: -22,
      autoAlpha: 0
    }, {
      x: 0,
      autoAlpha: 1,
      duration: .54,
      ease: 'power3.out',
      clearProps: 'transform,opacity,visibility'
    }, '-=.34').fromTo(letters, {
      yPercent: 105,
      scale: .62,
      rotationX: -48,
      rotationZ: (index) => index % 2 ? 5 : -5,
      autoAlpha: .08,
      filter: 'blur(8px)'
    }, {
      yPercent: 0,
      scale: 1,
      rotationX: 0,
      rotationZ: 0,
      autoAlpha: 1,
      filter: 'blur(0px)',
      duration: 1.12,
      stagger: .055,
      ease: 'elastic.out(1, .52)',
      clearProps: 'transform,opacity,visibility,filter'
    }, '-=.2');
    if (signature) {
      timeline.fromTo(signature, {
        y: 20,
        scaleX: .78,
        autoAlpha: 0
      }, {
        y: 0,
        scaleX: 1,
        autoAlpha: 1,
        duration: .72,
        ease: 'back.out(1.8)',
        clearProps: 'transform,opacity,visibility'
      }, '-=.52');
    }
    if (journey) {
      timeline.fromTo(journey, {
        y: 16,
        autoAlpha: 0
      }, {
        y: 0,
        autoAlpha: 1,
        duration: .58,
        ease: 'power3.out',
        clearProps: 'transform,opacity,visibility'
      }, '-=.38');
    }
  };

  const setupHeader = () => {
    const header = document.querySelector('#header-group');
    if (!header || header.dataset.vlmHeader === 'ready') return;
    header.dataset.vlmHeader = 'ready';
    let frame = 0;
    const update = () => {
      frame = 0;
      header.classList.toggle('vlm-is-scrolled', window.scrollY > 18);
    };
    window.addEventListener('scroll', () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  };

  const setupAmbientMotion = () => {
    if (lowMotionDevice) return;
    document.querySelectorAll('.vdh, .vpl, .vorliva-collection-stage, .vorliva-product-story, .vorliva-motion-gallery, .review-stack, .vorliva-trust-hub').forEach((section, sectionIndex) => {
      if (section.dataset.vlmAmbient === 'ready') return;
      section.dataset.vlmAmbient = 'ready';
      const field = document.createElement('div');
      field.className = 'vlm-ambient';
      field.setAttribute('aria-hidden', 'true');
      field.innerHTML = '<i></i><i></i><i></i>';
      section.prepend(field);
      const particles = toArray(field, 'i');
      const timeline = gsap.timeline({ paused: true, repeat: -1, yoyo: true });
      particles.forEach((particle, index) => {
        const direction = (sectionIndex + index) % 2 ? 1 : -1;
        timeline.to(particle, {
          xPercent: direction * (18 + index * 9),
          yPercent: direction * (-14 + index * 15),
          rotation: direction * (7 + index * 5),
          scale: 1 + index * .055,
          duration: 6.2 + index * 1.45,
          ease: 'sine.inOut'
        }, index * -.75);
      });
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => timeline.play(),
        onEnterBack: () => timeline.play(),
        onLeave: () => timeline.pause(),
        onLeaveBack: () => timeline.pause()
      });
    });
  };

  const setupHeroObjects = () => {
    if (lowMotionDevice) return;
    document.querySelectorAll('.vdh').forEach((hero) => {
      if (hero.dataset.vlmFloat === 'ready') return;
      hero.dataset.vlmFloat = 'ready';
      const images = toArray(hero, '.vdh__case-image');
      const shadows = toArray(hero, '.vdh__case-shadow');
      if (!images.length) return;
      const timeline = gsap.timeline({ paused: true });
      timeline.to(images, {
        y: -13,
        rotation: (index) => index % 2 ? -1.2 : 1.2,
        duration: 2.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        stagger: { each: .18, from: 'random' }
      }, 0).to(shadows, {
        scaleX: .86,
        autoAlpha: .54,
        duration: 2.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        stagger: { each: .18, from: 'random' }
      }, 0);
      ScrollTrigger.create({
        trigger: hero,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: () => timeline.play(),
        onEnterBack: () => timeline.play(),
        onLeave: () => timeline.pause(),
        onLeaveBack: () => timeline.pause()
      });
    });
  };

  const setupImageScrollMotion = () => {
    const selectors = refinementActive
      ? '.vpl__mobile-image img'
      : [
          '.vpl__mobile-image img',
          '.vorliva-product-story__frame img',
          '.review-stack__media img',
          '.vorliva-trust-hub__inline-image img'
        ].join(',');
    document.querySelectorAll(selectors).forEach((image) => {
      if (image.dataset.vlmScroll === 'ready') return;
      image.dataset.vlmScroll = 'ready';
      const trigger = image.closest('article, button, .vorliva-product-story__frame, .review-stack__media, .vorliva-trust-hub__inline-image') || image;
      gsap.timeline({
        scrollTrigger: {
          trigger,
          start: 'top 96%',
          end: 'bottom 16%',
          scrub: .58
        }
      }).fromTo(image, {
        y: 24,
        scale: .86,
        rotation: -1.2,
        autoAlpha: .38
      }, {
        y: 0,
        scale: 1,
        rotation: 0,
        autoAlpha: 1,
        duration: .72,
        ease: 'none'
      }).to(image, {
        y: -7,
        scale: .97,
        autoAlpha: .72,
        duration: .28,
        ease: 'none'
      });
    });
  };

  const setupRailPhysics = () => {
    document.querySelectorAll('.vorliva-collection-stage__track, .vorliva-motion-gallery__track, .review-stack__product-nav, .vorliva-trust-hub__notes-track').forEach((track) => {
      if (track.dataset.vlmRail === 'ready') return;
      track.dataset.vlmRail = 'ready';
      const images = toArray(track, 'img');
      let previous = track.scrollLeft;
      let settleTimer = 0;
      let frame = 0;

      const nativeProgress = track.parentElement?.querySelector('.vorliva-motion-gallery__mobile-progress');
      let progress = nativeProgress;
      if (!progress) {
        progress = document.createElement('span');
        progress.className = 'vlm-swipe-progress';
        progress.setAttribute('aria-hidden', 'true');
        progress.innerHTML = '<i></i>';
        track.insertAdjacentElement('afterend', progress);
      }
      const indicator = progress?.firstElementChild;

      const update = () => {
        frame = 0;
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const delta = track.scrollLeft - previous;
        previous = track.scrollLeft;
        if (progress) progress.hidden = maximum < 8;
        if (indicator && maximum >= 8) {
          const ratio = Math.max(0, Math.min(1, track.scrollLeft / maximum));
          if (progress.classList.contains('vlm-swipe-progress')) {
            indicator.style.setProperty('--vlm-thumb', Math.max(.18, track.clientWidth / track.scrollWidth).toFixed(4));
            indicator.style.setProperty('--vlm-progress', ratio.toFixed(4));
          } else {
            indicator.style.transform = `scaleX(${Math.max(.2, ratio)})`;
          }
        }
        if (!images.length) return;
        const rotation = Math.max(-2.4, Math.min(2.4, delta * .08));
        const shift = Math.max(-2.6, Math.min(2.6, -delta * .07));
        gsap.to(images, {
          rotation,
          xPercent: shift,
          scale: .985,
          duration: .13,
          ease: 'power1.out',
          overwrite: 'auto'
        });
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          gsap.to(images, {
            rotation: 0,
            xPercent: 0,
            scale: 1,
            duration: .82,
            ease: 'elastic.out(1, .46)',
            overwrite: 'auto'
          });
        }, 85);
      };

      track.addEventListener('scroll', () => {
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      }, { passive: true });
      window.requestAnimationFrame(update);

      if (images.length && !lowMotionDevice) {
        const bob = gsap.to(images, {
          y: (index) => index % 2 ? -6 : -9,
          duration: (index) => 2.7 + (index % 3) * .45,
          repeat: -1,
          yoyo: true,
          ease: 'sine.inOut',
          stagger: { each: .1, from: 'random' },
          paused: true
        });
        ScrollTrigger.create({
          trigger: track,
          start: 'top 96%',
          end: 'bottom 4%',
          onEnter: () => bob.play(),
          onEnterBack: () => bob.play(),
          onLeave: () => bob.pause(),
          onLeaveBack: () => bob.pause()
        });
      }
    });
  };

  const setupTapFeedback = () => {
    if (refinementActive) return;
    if (document.documentElement.dataset.vlmTap === 'ready') return;
    document.documentElement.dataset.vlmTap = 'ready';
    const selector = 'a[href], button:not([disabled]), summary';
    let active = null;
    const release = () => {
      if (!active) return;
      const icon = active.querySelector('svg, i, .vorliva-motion-gallery__arrow, .vorliva-collection-stage__arrow');
      const image = active.querySelector('.vorliva-motion-gallery__image--primary, .vorliva-collection-stage__image, .review-stack__product-thumb img');
      gsap.to(active, { scale: 1, duration: .46, ease: 'back.out(2.3)', overwrite: 'auto', clearProps: 'transform' });
      if (icon) {
        gsap.fromTo(icon, { y: 5, scale: .78, rotation: -8 }, {
          y: 0,
          scale: 1,
          rotation: 0,
          duration: .68,
          ease: 'elastic.out(1, .36)',
          overwrite: 'auto'
        });
      }
      if (image) {
        gsap.fromTo(image, { y: 7, scale: .92 }, {
          y: 0,
          scale: 1,
          duration: .72,
          ease: 'back.out(1.85)',
          overwrite: 'auto'
        });
      }
      active = null;
    };
    document.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      active = event.target.closest?.(selector);
      if (!active) return;
      gsap.to(active, { scale: .972, duration: .1, ease: 'power2.out', overwrite: 'auto' });
    }, { passive: true });
    document.addEventListener('pointerup', release, { passive: true });
    document.addEventListener('pointercancel', release, { passive: true });
  };

  const setupStateTransitions = () => {
    if (refinementActive) return;
    if (document.documentElement.dataset.vlmStates === 'ready') return;
    document.documentElement.dataset.vlmStates = 'ready';
    document.addEventListener('vorliva:case-change', () => {
      const active = document.querySelector('.vdh__case.is-active');
      if (!active) return;
      gsap.fromTo(toArray(active, '.vdh__case-image, .vdh__purchase > *'), {
        y: 30,
        scale: .86,
        rotation: -1.8,
        autoAlpha: .34
      }, {
        y: 0,
        scale: 1,
        rotation: 0,
        autoAlpha: 1,
        duration: .92,
        stagger: .06,
        ease: 'back.out(1.85)',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility'
      });
    });
    document.addEventListener('vorliva:feature-change', () => {
      const active = document.querySelector('.vpl__chapter.is-active');
      if (!active) return;
      gsap.fromTo(toArray(active, '.vpl__chapter-copy > *, .vpl__chapter-metric, .vpl__mobile-image img'), {
        y: 18,
        scale: .88,
        autoAlpha: .42
      }, {
        y: 0,
        scale: 1,
        autoAlpha: 1,
        duration: .76,
        stagger: .055,
        ease: 'back.out(1.8)',
        overwrite: 'auto',
        clearProps: 'transform,opacity,visibility'
      });
    });
    document.querySelectorAll('.review-stack').forEach((root) => {
      if (root.dataset.vlmReview === 'ready') return;
      root.dataset.vlmReview = 'ready';
      const animateActive = () => {
        const card = root.querySelector('.review-stack__card.is-active');
        if (!card) return;
        gsap.fromTo(toArray(card, '.review-stack__media img, .review-stack__product, .review-stack__review, .review-stack__button'), {
          y: 22,
          scale: .9,
          rotation: -1.2,
          autoAlpha: .35
        }, {
          y: 0,
          scale: 1,
          rotation: 0,
          autoAlpha: 1,
          duration: .86,
          stagger: .06,
          ease: 'back.out(1.85)',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility'
        });
      };
      const observer = new MutationObserver((records) => {
        if (records.some((record) => record.attributeName === 'class')) animateActive();
      });
      root.querySelectorAll('.review-stack__card').forEach((card) => observer.observe(card, { attributes: true, attributeFilter: ['class'] }));
    });
  };

  const initialize = () => {
    animateOpening();
    setupHeader();
    setupAmbientMotion();
    setupHeroObjects();
    setupImageScrollMotion();
    setupRailPhysics();
    setupTapFeedback();
    setupStateTransitions();
    window.requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  initialize();
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    const removed = event.target;
    ScrollTrigger.getAll().forEach((trigger) => {
      if (trigger.trigger && removed.contains(trigger.trigger)) trigger.kill();
    });
    gsap.killTweensOf([removed, ...toArray(removed, '*')]);
    window.requestAnimationFrame(() => ScrollTrigger.refresh());
  });
  document.addEventListener('vorliva:intro-complete', () => window.setTimeout(() => ScrollTrigger.refresh(), 80));
  window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      gsap.ticker.sleep();
    } else {
      gsap.ticker.wake();
      window.requestAnimationFrame(() => ScrollTrigger.refresh());
    }
  });
})();
